import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { problemDetailCode, toFormError } from '@rms/core/error';
import { AppError } from '@rms/sdk/errors';
import { catchError, first, map, Observable, switchMap, throwError } from 'rxjs';

import { InvalidCurrentPasswordError, UserEmailInUseError } from '../errors';
import {
  PasswordChangeRequest,
  User,
  USER_ERROR_CODE,
  USER_ROLE,
  UserProfile,
  UserProfileUpdateRequest,
} from '../models';
import { UserStoreService } from '../store';
import { UserApiService } from './user-api.service';

/** The signed-in user's own profile: supervisors use `/v1/users`, partner roles `/v1/partners/users/me`. */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly api = inject(UserApiService);
  private readonly userStore = inject(UserStoreService);

  public getProfile(): Observable<UserProfile> {
    return this.signedIn().pipe(
      switchMap(({ id, isSupervisor }) => (isSupervisor ? this.api.getUser(id) : this.api.getMe())),
      catchError((error: HttpErrorResponse) => throwError(() => AppError.from(error, 'profile.errors.load')))
    );
  }

  public updateProfile(request: UserProfileUpdateRequest): Observable<UserProfile> {
    return this.signedIn().pipe(
      switchMap(({ id, isSupervisor }) =>
        isSupervisor ? this.api.updateUser(id, request) : this.api.updateMe(request)
      ),
      catchError((error: HttpErrorResponse) =>
        throwError(() =>
          problemDetailCode(error) === USER_ERROR_CODE.EMAIL_ALREADY_IN_USE
            ? new UserEmailInUseError(error)
            : toFormError(error, 'profile.errors.update')
        )
      )
    );
  }

  public changePassword(request: PasswordChangeRequest): Observable<void> {
    return this.signedIn().pipe(
      switchMap(({ isSupervisor }) =>
        isSupervisor ? this.api.changePassword(request) : this.api.changeMyPassword(request)
      ),
      catchError((error: HttpErrorResponse) =>
        throwError(() =>
          problemDetailCode(error) === USER_ERROR_CODE.INVALID_CURRENT_PASSWORD
            ? new InvalidCurrentPasswordError(error)
            : toFormError(error, 'profile.errors.password')
        )
      )
    );
  }

  private signedIn(): Observable<Pick<User, 'id'> & { isSupervisor: boolean }> {
    return this.userStore.user$.pipe(
      first(),
      map(({ id, roles }) => ({ id, isSupervisor: roles.includes(USER_ROLE.ROLE_SUPERVISOR) }))
    );
  }
}
